
import { useEffect, useMemo } from "react";
import { Toaster } from "react-hot-toast";
import { AgentGraph } from "@/components/AgentGraph";
import { ChatWindow } from "@/components/ChatWindow";
import { ChatInput } from "@/components/ChatInput";
// import { ExcelUploader } from "@/components/ExcelUploader";
import { ConversationSidebar } from "@/components/ConversationSidebar";
import { ReportsPanel } from "@/components/ReportsPanel";
import { api } from "@/services/api";
import { useAgentStore } from "@/store/agentStore";
import { useTheme } from "@/context/ThemeContext";
import "./App.css";

export default function App() {
  const phase = useAgentStore((s) => s.current?.phase);
  const dataSource = useAgentStore((s) => s.dataSource);
  const setDataSource = useAgentStore((s) => s.setDataSource);
  const setWarehouseId = useAgentStore((s) => s.setWarehouseId);
  const setUserId = useAgentStore((s) => s.setUserId);
  const setSavedSessions = useAgentStore((s) => s.setSavedSessions);
  const { theme, toggleTheme } = useTheme();

  const { warehouseId, userId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      warehouseId: params.get("warehouseId"),
      userId: params.get("userId"),
    };
  }, []);

  useEffect(() => {
    setWarehouseId(warehouseId);
    setUserId(userId);
  }, [warehouseId, userId, setWarehouseId, setUserId]);

  // Load saved sessions on mount scoped to this user (and warehouseId if present)
  useEffect(() => {
    api.listSessions({ userId: userId ?? undefined, warehouseId: warehouseId ?? undefined })
      .then(setSavedSessions)
      .catch(() => {});
  }, [userId, warehouseId, setSavedSessions]);

  const statusDotClass = `status-dot${phase === "running" ? " status-dot--running" : phase === "done" ? " status-dot--done" : ""}`;
  const statusText = phase === "running" ? "Analyzing…" : phase === "done" ? "Complete" : phase === "error" ? "Error" : "Idle";

  const toastBg = theme === "dark" ? "#0f172a" : "#ffffff";
  const toastColor = theme === "dark" ? "#f8fafc" : "#0f172a";
  const toastBorder = theme === "dark" ? "1px solid #1e293b" : "1px solid #e2e8f0";

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: toastBg,
            color: toastColor,
            border: toastBorder,
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
          },
        }}
      />

      <div className="app-layout">

        {/* ── Topbar ── */}
        <header className="app-header">
          <div className="app-logo">
            <div className="app-logo-icon">⚡</div>
            <div>
              <span className="app-logo-title">
                Business <span className="accent">Intelligence</span>
              </span>
              <span className="app-logo-badge">Xenith</span>
            </div>
          </div>

          <div className="app-header-right">
            <div className="status-pill">
              <div className={statusDotClass} />
              <span className="status-label">{statusText}</span>
            </div>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? "☀" : "🌙"}
            </button>
          </div>
        </header>

        {/* ── Body ── */}
        <div className="app-body">

          {/* Conversation history sidebar */}
          <ConversationSidebar />

          {/* Left sidebar - Data source + Agent pipeline + Reports */}
          <aside className="app-sidebar">
            {/* Data source selector */}
            <section>
              <div className="sidebar-label">DATA SOURCE</div>
              <div className="datasource-buttons">
                {(["db"/*, "excel"*/] as const).map((src) => (
                  <button
                    key={src}
                    onClick={() => setDataSource(src)}
                    className={`datasource-btn${dataSource === src ? " datasource-btn--active" : ""}`}
                  >
                    {src === "db" ? "⚡ Database" : "📊 Excel"}
                  </button>
                ))}
              </div>

              {/*dataSource === "excel" && (
                <div className="excel-uploader-wrapper">
                  <ExcelUploader />
                </div>
              )*/}
            </section>

            {/* Agent pipeline */}
            <section className="pipeline-section">
              <div className="sidebar-label sidebar-label--pipeline">AGENT PIPELINE</div>
              <div className="pipeline-scroll">
                <AgentGraph />
              </div>
            </section>

            {/* Predefined reports */}
            <ReportsPanel warehouseId={warehouseId ?? undefined} />
          </aside>

          {/* Main chat area */}
          <main className="app-main">
            <ChatWindow />
            <footer className="app-footer">
              <ChatInput />
            </footer>
          </main>

        </div>
      </div>
    </>
  );
}
